import { useMemo, useCallback } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const useEmployeeHandlers = ({
  individuals = [],
  sendTableDataToParent,
  setError,
  examid,
  searchTerm = "",
  filters = { departments: [], status: [] },
  page,
  setPage,
  selectedEmployees = [],
  setSelectedEmployees,
  notFoundEmployees = [],
  setNotFoundEmployeeCodes,
  openNotFoundPopup,
  setOpenNotFoundPopup,
  openSuccessPopup,
  setOpenSuccessPopup,
  setUploadErrorMessage,
  setOpenUploadErrorPopup,
  rowsPerPage = 10,
  users,
}) => {
  const getSearchableStatus = useCallback((user = {}) => {
    if (user.isAssignedElsewhere) return "Already Assigned";
    if (user.assessmentStatus) return user.assessmentStatus;
    return user.status || "Available";
  }, []);

  const getStatusSearchAlias = useCallback((user = {}) => {
    if (user.assessmentStatus === "Pending Assessment") return "Pending Attempts";
    return "";
  }, []);

  // const handleAddEmployees = useCallback((employeesToAdd = selectedEmployees) => {
  //   if (!employeesToAdd || employeesToAdd.length === 0) return;
  //   const newRows = employeesToAdd
  //     .filter(emp => !individuals.some(existing => existing.id === emp.id))
  //     .map(emp => ({ ...emp, status: "Pending", assignmentId: 1 }));
  //   const updated = [...individuals, ...newRows];
  //   sendTableDataToParent?.(updated);
  //   setSelectedEmployees([]);
  // }, [selectedEmployees, individuals, sendTableDataToParent, setSelectedEmployees]);

  const handleAddEmployees = useCallback((employeesToAdd = selectedEmployees) => {
    if (!employeesToAdd || employeesToAdd.length === 0) return;

    const updatedEmployees = new Map(
      (individuals || []).map(employee => [employee.id, employee])
    );

    employeesToAdd
      .filter(emp => emp?.id)
      .forEach(emp => {
        const existing = updatedEmployees.get(emp.id);
        if (existing?.isActive === true) return;

        updatedEmployees.set(emp.id, {
        ...emp,
        status: "Pending",
        assignmentId: 1,
        isActive: true,
        wasPreviouslyRemoved: false,
        isAlreadyAdded: false,
        });
      });

    sendTableDataToParent?.([...updatedEmployees.values()]);

    // ✅ clear AFTER update
    setSelectedEmployees([]);
  }, [selectedEmployees, individuals, sendTableDataToParent, setSelectedEmployees]);


  const handleRemoveEmployee = useCallback(
    (id) => {
      const updated = individuals.filter(emp => emp.id !== id);
      sendTableDataToParent?.(updated);
    },
    [individuals, sendTableDataToParent]
  );

  const handleUploadExcel = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      try {
        const allInserted = [];
        const allNotFound = [];

        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("examid", examid);

          const res = await axios.post(
            "/coordinators/upload-employees-excel/",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          allInserted.push(...(res.data.inserted || []));
          allNotFound.push(...(res.data.not_found || []));
        }

        const formattedInserted = allInserted.map((emp) => ({
          id: emp.Id,
          employeeId: emp.EmployeeId,
          employeeName: emp.EmployeeName,
          departmentId: emp.DepartmentId,
          departmentName: emp.DepartmentName,
          userAttemptCount: emp.UserAttemptCount ?? emp.userAttemptCount ?? 0,
          attemptCount: emp.AttemptCount ?? emp.attemptCount ?? 1,
          status: "Scheduled",
          isActive: true,
          assignmentId: 1,
        }));

        // Preserve existing employees and remove duplicates across files.
        const employeeMap = new Map();

        [...individuals, ...formattedInserted].forEach((employee) => {
          employeeMap.set(employee.id, employee);
        });

        sendTableDataToParent?.([...employeeMap.values()]);

        const uniqueNotFound = [...new Set(allNotFound)];

        if (uniqueNotFound.length > 0) {
          setNotFoundEmployeeCodes(uniqueNotFound);
          setOpenNotFoundPopup(true);
        } else {
          setOpenSuccessPopup(true);
        }
      } catch (err) {
        console.error("Error uploading Excel files:", {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
        });

        setUploadErrorMessage?.(
          err.response?.data?.error ||
          "Failed to upload one or more Excel files. Please try again."
        );
        setOpenUploadErrorPopup?.(true);
      } finally {
        e.target.value = "";
      }
    },
    [
      examid,
      individuals,
      sendTableDataToParent,
      setNotFoundEmployeeCodes,
      setOpenNotFoundPopup,
      setOpenSuccessPopup,
      setUploadErrorMessage,
      setOpenUploadErrorPopup,
    ]
  );
  // const handleUploadExcel = useCallback(
  //   async (e) => {
  //     const file = e.target.files[0];
  //     if (!file) return;

  //     const formData = new FormData();
  //     formData.append("file", file);
  //     formData.append("examid", examid);
  //     try {
  //       console.log("Uploading Excel to:", `/coordinators/upload-employees-excel/`);
  //       const res = await axios.post(`/coordinators/upload-employees-excel/`, formData, {
  //         headers: { "Content-Type": "multipart/form-data" },

  //       });
  //       const { inserted, not_found } = res.data;
  //       const formattedInserted = inserted.map(emp => ({
  //         id: emp.Id,
  //         employeeId: emp.EmployeeId,
  //         employeeName: emp.EmployeeName,
  //         departmentId: emp.DepartmentId,
  //         departmentName: emp.DepartmentName,
  //         status: "Scheduled",
  //         isActive: true,
  //         assignmentId: 1,
  //       }));
  //       sendTableDataToParent?.(formattedInserted);
  //       if (not_found.length > 0) {
  //         setNotFoundEmployeeCodes(not_found);
  //         setOpenNotFoundPopup(true);
  //       } else {
  //         setOpenSuccessPopup(true);
  //       }
  //     } catch (err) {
  //       console.error("Error uploading Excel:", {
  //         status: err.response?.status,
  //         data: err.response?.data,
  //         message: err.message,
  //       });
  //       alert(err.response?.data?.error || "Failed to upload Excel. Please try again.");
  //     } finally {
  //       e.target.value = null;
  //     }
  //   },
  //   [examid, sendTableDataToParent, setNotFoundEmployeeCodes, setOpenNotFoundPopup, setOpenSuccessPopup]
  // );

  const handleDownloadTemplate = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("EmployeeCode");
    sheet.addRow(["S.No", "Employee Code"]);
    for (let i = 1; i <= 100; i++) sheet.addRow([i, ""]);
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "EmployeeCodes.xlsx");
  }, []);

  const filteredIndividuals = useMemo(() => {
    if (!Array.isArray(individuals)) {
      console.warn("useEmployeeHandlers: individuals is not an array:", individuals);
      return [];
    }
    return individuals.filter((user) => {
      const normalizedSearch = searchTerm.toLowerCase();
      const matchesSearch = [
        user.employeeName,
        user.departmentName,
        user.employeeId,
        user.status,
        user.assessmentStatus,
        getSearchableStatus(user),
        getStatusSearchAlias(user),
      ].some(value => String(value || "").toLowerCase().includes(normalizedSearch));
      const matchesDepartment = !filters.departments?.length || filters.departments.includes(user.departmentName);
      const matchesStatus = !filters.status?.length || filters.status.includes(user.status);
      return matchesSearch && matchesDepartment && matchesStatus;
    });
  }, [individuals, searchTerm, filters, getSearchableStatus, getStatusSearchAlias]);

  const totalPages = useMemo(() => {
    const count = filteredIndividuals.length;
    console.log("useEmployeeHandlers: filteredIndividuals length:", count);
    return count > 0 ? Math.ceil(count / rowsPerPage) : 0;
  }, [filteredIndividuals.length, rowsPerPage]);

  const currentRows = useMemo(
    () => filteredIndividuals.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [filteredIndividuals, page, rowsPerPage]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      if (Number.isFinite(totalPages) && newPage >= 1 && newPage <= totalPages) {
        setPage(newPage);
      }
    },
    [totalPages, setPage]
  );

  const renderPageNumbers = useCallback(() => {
    if (!Number.isFinite(totalPages) || totalPages === 0) {
      console.log("useEmployeeHandlers: renderPageNumbers returning empty array, totalPages:", totalPages);
      return [];
    }
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    return pages;
  }, [page, totalPages]);

  const handleDeleteEmployee = useCallback(
    (id) => {
      const updated = individuals.map(emp =>
        emp.id === id ? { ...emp, isActive: false } : emp
      );
      setSelectedEmployees(previous => previous.filter(emp => emp.id !== id));
      sendTableDataToParent?.(updated);
    },
    [individuals, sendTableDataToParent, setSelectedEmployees]
  );

  const handleUndoEmployee = useCallback(
    (id) => {
      const updated = individuals.map(emp =>
        emp.id === id ? { ...emp, isActive: true } : emp
      );
      sendTableDataToParent?.(updated);
    },
    [individuals, sendTableDataToParent]
  );


  return {
    handleAddEmployees,
    handleRemoveEmployee,
    handleUploadExcel,
    handleDownloadTemplate,
    filteredIndividuals,
    totalPages,
    currentRows,
    handlePageChange,
    renderPageNumbers,
    rowsPerPage,
    handleUndoEmployee,
    handleDeleteEmployee
  };
};
